// RoomPhotoViewer — full-screen display-only photo viewer (now multi-photo).
//
// Renders the room photo(s) at maximum scale with orientation chrome
// (success pill, Done button, "coming soon" hint) plus an X for cancel.
// Multi-photo: CSS scroll-snap gallery, native iOS swipe gesture, dots
// indicator at bottom. No gestures on the photo itself yet — pinch-to-zoom
// and "show what Casavio noticed" overlay land in the next session.
//
// X (top-right) is the "abandon/cancel" affordance. Done (bottom amber
// primary) is the "I'm finished, save and exit" affordance. Both fire
// onClose — semantically distinct, behaviorally identical for tonight.
//
// Props:
//   photoUrls      — string[] of URLs (signed Supabase OR local blob:).
//                    Preferred input. Empty array = nothing to show.
//   photoUrl       — string, single-URL convenience for old call sites
//                    (Room Photos grid). Wrapped into [photoUrl].
//   onClose        — () => void, fires when X / Done / backdrop tapped
//   alt            — optional accessibility label (defaults to '')
//   justCaptured   — boolean. When true, show "✓ Photo saved" / "✓ N
//                    photos saved" pill that auto-fades after 2.5s.

import { useEffect, useRef, useState } from 'react'
import { X, Check } from 'lucide-react'

const C = {
  amber: '#B8721F', amberLight: '#D4902A',
  successText: '#2E6B4A', successBg: '#EBF5EF',
  muted: '#9A7B68',
}
const f = 'Georgia, serif'
const fs = 'Calibri, sans-serif'

export default function RoomPhotoViewer({
  photoUrls,
  photoUrl,
  onClose,
  alt = '',
  justCaptured = false,
}) {
  // Normalize input — accept either array prop or single string prop.
  const urls = Array.isArray(photoUrls)
    ? photoUrls.filter(Boolean)
    : (photoUrl ? [photoUrl] : [])

  // Currently-visible photo index, driven by the gallery's scroll position.
  const [currentIndex, setCurrentIndex] = useState(0)
  const galleryRef = useRef(null)

  const handleGalleryScroll = (e) => {
    const w = e.currentTarget.clientWidth
    if (w <= 0) return
    const idx = Math.round(e.currentTarget.scrollLeft / w)
    if (idx !== currentIndex && idx >= 0 && idx < urls.length) {
      setCurrentIndex(idx)
    }
  }

  // Saved-pill auto-fade. Only fires when justCaptured is true.
  const [pillVisible, setPillVisible] = useState(justCaptured)
  useEffect(() => {
    if (!justCaptured) return
    setPillVisible(true)
    const t = setTimeout(() => setPillVisible(false), 2500)
    return () => clearTimeout(t)
  }, [justCaptured])

  if (urls.length === 0) return null

  const isMulti = urls.length > 1
  const pillText = urls.length === 1 ? 'Photo saved' : `${urls.length} photos saved`

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: '#000',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Gallery area — scroll-snap on x, one full-width slide per photo.
          For a single photo this collapses to the same behavior as before. */}
      <div
        ref={galleryRef}
        onScroll={handleGalleryScroll}
        style={{
          flex: 1, minHeight: 0,
          display: 'flex',
          overflowX: isMulti ? 'auto' : 'hidden',
          overflowY: 'hidden',
          scrollSnapType: isMulti ? 'x mandatory' : undefined,
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
        }}
      >
        {urls.map((url, i) => (
          <div
            key={`${url}-${i}`}
            onClick={e => { if (e.target === e.currentTarget) onClose() }}
            style={{
              flex: '0 0 100%',
              scrollSnapAlign: 'start',
              scrollSnapStop: 'always',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '12px',
            }}
          >
            <img
              src={url}
              alt={alt}
              style={{
                maxWidth: '100%', maxHeight: '100%',
                width: 'auto', height: 'auto',
                objectFit: 'contain', display: 'block',
              }}
            />
          </div>
        ))}
      </div>

      {/* Saved confirmation pill — top-center, fades after 2.5s. */}
      {justCaptured && pillVisible && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(20px + env(safe-area-inset-top))',
            left: '50%', transform: 'translateX(-50%)',
            background: C.successBg, color: C.successText,
            padding: '8px 16px', borderRadius: 999,
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontFamily: fs, fontSize: 13, fontWeight: 700,
            letterSpacing: 0.2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
            transition: 'opacity 400ms ease-out',
            pointerEvents: 'none',
          }}
        >
          <Check size={14} strokeWidth={3} color={C.successText} />
          {pillText}
        </div>
      )}

      {/* X cancel button — top-right. */}
      <button
        onClick={onClose}
        aria-label="Close photo"
        style={{
          position: 'absolute',
          top: 'calc(12px + env(safe-area-inset-top))',
          right: 'calc(12px + env(safe-area-inset-right))',
          width: 44, height: 44, borderRadius: '50%',
          background: 'rgba(0,0,0,0.55)',
          border: '1px solid rgba(255,255,255,0.18)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', padding: 0, lineHeight: 0,
        }}
      >
        <X size={22} color="#fff" strokeWidth={2.2} />
      </button>

      {/* Bottom chrome: dots + hint + Done. */}
      <div
        style={{
          flexShrink: 0,
          padding: '12px 18px calc(20px + env(safe-area-inset-bottom))',
          background: 'rgba(0,0,0,0.55)',
          display: 'flex', flexDirection: 'column', gap: 10,
          alignItems: 'center',
        }}
      >
        {isMulti && (
          <div
            aria-label={`Photo ${currentIndex + 1} of ${urls.length}`}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            {urls.map((_, i) => (
              <div
                key={i}
                style={{
                  width: 7, height: 7, borderRadius: '50%',
                  background: i === currentIndex ? '#fff' : 'rgba(255,255,255,0.35)',
                  transition: 'background 180ms ease-out',
                }}
              />
            ))}
          </div>
        )}
        <div
          style={{
            fontFamily: fs, fontSize: 11, color: 'rgba(255,255,255,0.65)',
            letterSpacing: 0.2, textAlign: 'center',
          }}
        >
          Interactive identification coming soon.
        </div>
        <button
          onClick={onClose}
          style={{
            width: '100%', maxWidth: 320,
            background: `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
            color: '#fff', border: 'none', borderRadius: 10,
            padding: 14, fontFamily: f, fontSize: 15,
            fontWeight: 700, cursor: 'pointer', letterSpacing: 0.3,
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
          }}
        >
          Done
        </button>
      </div>
    </div>
  )
}
